import { EnumScreenName } from '@calm/evexia-analytics';
import { CirclePartnerHealthResource } from '@calm/evexia-web-sdk';
import { ChLink } from '@rhg/components/ch-link';
import { Heading } from '@rhg/components/heading';
import messages from '@rhg/components/resources/messages';
import commonMessages from '@rhg/lang/messages/common';
import { Button, Theme } from '@rhg/libs/material';
import { routes } from '@rhg/utils/routes';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

const styleHeading = {
    mb: (theme: Theme) => theme.ds.spacingUnits.CONTENT,
};

interface ResourcesCarouselHeadingProps {
    variant: 'list' | 'card' | 'smart' | undefined;
    partnerDisplayName?: string;
    resources: CirclePartnerHealthResource[];
    source?: string;
}

export const RESOURCES_VIEW_ALL_THRESHOLD = 3;

export default function ResourcesCarouselHeading({
    variant,
    partnerDisplayName,
    resources,
    source,
}: ResourcesCarouselHeadingProps) {
    const { formatMessage } = useIntl();
    const serviceString = useMemo(
        () =>
            resources.length === 1
                ? formatMessage(messages['resources.carousel.label.service'])
                : formatMessage(messages['resources.carousel.label.services']),
        [resources.length, formatMessage]
    );

    return (
        <Heading
            testId="resources-heading"
            titleAriaLabel={formatMessage(
                messages['resources.carousel.title.aria_label']
            )}
            title={
                variant === 'smart'
                    ? formatMessage(messages['resources.carousel.smart.title'])
                    : formatMessage(messages['resources.carousel.title'])
            }
            titleVariant="h5"
            titleComponent="h2"
            subtitle={
                variant === 'smart'
                    ? formatMessage(
                          messages['resources.carousel.smart.subtitle'],
                          {
                              service: serviceString,
                              sponsor: partnerDisplayName,
                          }
                      )
                    : formatMessage(messages['resources.carousel.subtitle'], {
                          number: resources.length,
                          service: serviceString,
                          sponsor: partnerDisplayName,
                      })
            }
            rightButton={
                resources.length > RESOURCES_VIEW_ALL_THRESHOLD ? (
                    <Button
                        href={routes.resources}
                        trackScreen={source as EnumScreenName}
                        trackButton="ViewAllResources"
                        color="primary"
                        component={ChLink}
                        size="medium"
                        variant="text"
                        aria-label={formatMessage(
                            messages['resources.carousel.view_all.aria_label']
                        )}
                    >
                        {formatMessage(commonMessages['common.view_all'])}
                    </Button>
                ) : undefined
            }
            sx={variant === 'card' ? styleHeading : undefined}
        />
    );
}
